/**
 * My Trades' Polymarket US realized P&L (#1031), in the K/P headline shape, over the same rows the
 * page lists (#1227, `buildUsTradeVMs`), as `computePolyNetPnlSummary` sums the Poly rows: only
 * realized rows count (SELL and SETTLED; an OPEN row is not realized yet), each in the window by its
 * own date. A realized row with an unknown net (a settled position whose payout is not verified yet,
 * or one whose cost basis or fees are unknown, or an orphan sale) is pending, and a pending row makes
 * the venue's amount null — never a partial total, never counted as 0. Sums are exact (12 places, as
 * the relay); only the final total is rounded to cents.
 */

import type { TradeCardVM } from '@/components/cards/TradeCard';
import type { UsMyTradePosition } from '@/lib/polymarketUs/myTradesClient';
import { signedDecimalUnits } from '@/lib/polymarketUs/ticketModel';
import { buildUsTradeVMs } from './usTradeRows';

export interface UsPnlSummary {
  /** Sum of the in-range realized rows in cents; null when any of them is pending. */
  netPnlCents: number | null;
  /** SELL and SETTLED rows in range. */
  closedCount: number;
  /** Realized rows in range whose net is unknown (the headline's "N … pending", as K/P count rows). */
  pendingCount: number;
  /** Positions with a SETTLED row whose P&L waits on a verified payout (the notes name positions). */
  pendingPayoutCount: number;
  /** Other positions with a realized row whose net is unknown (cost basis or fees unknown, orphan sale). */
  incompleteCount: number;
}

const SCALE = 10n ** 12n;

/** Exact 12-place units to whole cents, half away from zero. */
function toCents(total: bigint): number {
  const perCent = SCALE / 100n;
  const negative = total < 0n, abs = negative ? -total : total;
  const cents = (abs + perCent / 2n) / perCent;
  return Number(negative ? -cents : cents);
}

/**
 * The US rows the page shows: every position's K/P rows, each kept by its own date (a sale and the
 * settlement after it can fall in different windows, as Poly's rows do), and the Closed view hides
 * only OPEN rows (R10-F3), so a sale of a still-held position stays listed. A row with no time at
 * all is shown only when the window is unbounded.
 */
export function usTradeRowsInView(positions: readonly UsMyTradePosition[], startMs: number, endMs: number,
  view: 'closed' | 'all'): TradeCardVM[] {
  return positions.flatMap(buildUsTradeVMs)
    .filter((vm) => (vm.closedAtMs === 0 ? startMs <= 0 && endMs === Infinity : vm.closedAtMs >= startMs && vm.closedAtMs <= endMs))
    .filter((vm) => view === 'all' || vm.action !== 'OPEN');
}

export function summarizeUsPnl(rows: readonly TradeCardVM[]): UsPnlSummary {
  let total = 0n, closedCount = 0, pendingCount = 0;
  // A position's rows share its key up to the action; a position is named once in the notes. A
  // payout-unverified position's SELL row is always known (its cost and fees are), so the two sets
  // never share a position.
  const payout = new Set<string>(), incomplete = new Set<string>();
  for (const vm of rows) {
    if (!vm.us || (vm.action !== 'SELL' && vm.action !== 'SETTLED')) continue;
    closedCount += 1;
    const net = vm.us.netPnlText === null ? null : signedDecimalUnits(vm.us.netPnlText);
    if (net === null) {
      pendingCount += 1;
      const position = vm.key.slice(0, vm.key.lastIndexOf('|'));
      (vm.us.pending === 'payout' ? payout : incomplete).add(position);
      continue;
    }
    total += net;
  }
  return { netPnlCents: pendingCount > 0 ? null : toCents(total), closedCount, pendingCount,
    pendingPayoutCount: payout.size, incompleteCount: incomplete.size };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The US section's notes: which US values are unknown, and why, and how many executions were withheld. */
export function usPnlNotes(summary: Pick<UsPnlSummary, 'pendingPayoutCount' | 'incompleteCount'>, withheld: number): string[] {
  const notes: string[] = [];
  if (summary.pendingPayoutCount > 0) {
    notes.push(`${plural(summary.pendingPayoutCount, 'settled US position', 'settled US positions')}: P&L pending verified payout`);
  }
  if (summary.incompleteCount > 0) {
    notes.push(`${plural(summary.incompleteCount, 'US position', 'US positions')}: P&L incomplete`);
  }
  if (withheld > 0) notes.push(`${plural(withheld, 'US execution', 'US executions')} withheld`);
  return notes;
}
